import { RubyGemMark } from '../../components/RubyGemMark';
/**
 * Builds tab — published builds per app/target/env, newest first.
 *
 * `GET /builds` requires `appId` + `env` (release-service has no Scan — see
 * task-D2-report.md), so this tab first calls `GET /apps` to discover every
 * (appId, env) pair that has ever had a build registered, then fans out one
 * `GET /builds` per pair. `Promise.allSettled` — one env erroring (e.g. a
 * transient 5xx) must not blank the builds that DID load for every other
 * pair; that failure is reported inline instead.
 *
 * `BuildRecordSchema` (@platform/shared-types) has no "published by" actor
 * field — `POST /builds` is gated by `INTERNAL_API_KEY` only (see
 * release-service/src/lib/auth.ts), so every row here was written by CI,
 * never a human operator. That column is therefore gone: it could only ever
 * print one value. In its place is the COMMENT column — the commit subject CI
 * now registers (`commitMessage` + `commit`), falling back to the operator's
 * own `label` when a row predates those fields or CI could not read git.
 *
 * There is deliberately NO status column. registerBuild.ts writes a row only
 * when registration SUCCEEDS, so a failed build is ABSENT from this table, not
 * present-and-failed — a stored status could only say "success" and would tell
 * an operator nothing they could not read from the row existing at all.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, PackageOpen, Pencil, RefreshCw, X } from 'lucide-react';
import { releaseApi } from '../../api/releaseApi';
import { buildShortId, type BuildRecord } from '@platform/shared-types';
import { RubyLoader } from '../../components/RubyLoader';

const DISPLAY_LIMIT = 100;

interface Row extends BuildRecord {
  _key: string;
}

/**
 * The label cell — a build's human name, renameable in place.
 *
 * Custom controls only: no `prompt()`. An iOS webview suppresses native
 * dialogs outright (feedback_no_native_confirm_ios_webview), and the platform
 * rule is custom controls everywhere regardless.
 *
 * The saved label is held by the PARENT, not here: a reload of the table must
 * not silently revert a rename that succeeded, and a failed save must not
 * leave the table showing a label the server never stored. This component owns
 * only the draft text while editing.
 */
const BuildLabelCell: React.FC<{
  row: Row;
  onSave: (label: string | null) => Promise<void>;
}> = ({ row, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.label || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const open = () => { setDraft(row.label || ''); setError(null); setEditing(true); };
  const cancel = () => { setEditing(false); setError(null); };

  const commit = async () => {
    const trimmed = draft.trim();
    // An emptied box means "clear it" — sent as an explicit null, never
    // undefined, or the server would keep the old label.
    const next = trimmed ? trimmed : null;
    if (next === (row.label || null)) { setEditing(false); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={open}
        title={row.label ? 'Rename this build' : 'Give this build a name'}
        className="group inline-flex items-center gap-1.5 text-left min-h-[44px] px-1 -mx-1 rounded-md hover:bg-stone-100 max-w-[260px]"
      >
        {row.label ? (
          <span className="text-stone-800 truncate">{row.label}</span>
        ) : (
          <span className="text-stone-400 italic">Add a label</span>
        )}
        <Pencil size={12} className="shrink-0 text-stone-300 group-hover:text-rose-500" />
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-[220px]">
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          value={draft}
          maxLength={120}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void commit(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          placeholder="e.g. Cash discount fix"
          className="flex-1 min-h-[44px] px-2 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 disabled:bg-stone-100"
        />
        <button
          onClick={() => void commit()}
          disabled={saving}
          title="Save"
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
        >
          {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Check size={15} />}
        </button>
        <button
          onClick={cancel}
          disabled={saving}
          title="Cancel"
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg border border-stone-300 text-stone-500 hover:bg-stone-100 disabled:opacity-50"
        >
          <X size={15} />
        </button>
      </div>
      <p className="text-[11px] text-stone-400">Empty clears the label. Enter saves, Esc cancels.</p>
      {error && <p className="text-[11px] text-red-600">{error}</p>}
    </div>
  );
};

/**
 * The comment cell — what this build WAS, in words.
 *
 * Precedence is commit subject first, operator label second. CI's subject is
 * the truth about the code; the label is a nickname someone typed. A row with
 * neither is a build registered before `commitMessage` existed (or one where
 * git could not be read in CI), and renders as an explicit em-dash rather than
 * blank, so "not recorded" is visibly different from "empty".
 *
 * The sha is shown short (7 chars, git's own convention) with the full value on
 * hover — it is provenance you occasionally paste into `git show`, not
 * something you read across a table.
 */
const BuildCommentCell: React.FC<{ row: Row }> = ({ row }) => {
  const text = row.commitMessage || row.label;
  const shortSha = row.commit ? row.commit.slice(0, 7) : null;

  return (
    <div className="max-w-[320px]">
      {text ? (
        <span className="block text-stone-700 truncate" title={text}>{text}</span>
      ) : (
        <span className="block text-stone-400">—</span>
      )}
      {shortSha && (
        <span className="block font-mono text-[11px] text-stone-400" title={row.commit}>
          {shortSha}
          {!row.commitMessage && text ? ' · label' : ''}
        </span>
      )}
    </div>
  );
};

const BuildsTab: React.FC = () => {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partialErrors, setPartialErrors] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPartialErrors([]);
    try {
      const appsRes = await releaseApi.apps.list();
      const pairs: { appId: string; env: string }[] = [];
      for (const app of appsRes.apps || []) {
        for (const env of app.environments) {
          pairs.push({ appId: app.appId, env: env.env });
        }
      }

      if (pairs.length === 0) {
        setRows([]);
        return;
      }

      const settled = await Promise.allSettled(
        pairs.map((p) => releaseApi.builds.list(p.appId, p.env)),
      );

      const collected: Row[] = [];
      const errs: string[] = [];
      settled.forEach((result, i) => {
        const { appId, env } = pairs[i];
        if (result.status === 'fulfilled') {
          for (const b of result.value.items || []) {
            collected.push({ ...b, _key: `${appId}:${env}:${b.target}:${b.version}` });
          }
        } else {
          errs.push(`${appId} (${env}): ${result.reason?.message || 'failed to load'}`);
        }
      });

      collected.sort((a, b) => (b.builtAt ?? b.registeredAt ?? 0) - (a.builtAt ?? a.registeredAt ?? 0));
      setRows(collected);
      setPartialErrors(errs);
    } catch (e: any) {
      setError(e?.message || 'Failed to load builds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * Persist a rename, then reflect it locally.
   *
   * The local row is updated ONLY after the server confirms — an optimistic
   * write here would leave the table showing a label that was never stored if
   * the PATCH failed, and this table is what an operator uses to tell two
   * builds apart. The error is re-thrown so the cell can show it inline and
   * stay open on the text the operator typed.
   */
  const saveLabel = useCallback(async (row: Row, label: string | null) => {
    await releaseApi.builds.setLabel({
      appId: row.appId, env: row.env, target: row.target, version: row.version, label,
    });
    setRows((prev) => (prev || []).map((r) => (
      r._key === row._key ? { ...r, label: label ?? undefined } : r
    )));
  }, []);

  if (loading && rows === null) {
    return (
      <RubyLoader variant="page" label="Loading builds…" />
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
        <AlertTriangle size={28} className="mx-auto text-red-500 mb-2" />
        <p className="text-sm text-stone-700 font-medium mb-1">Could not load builds</p>
        <p className="text-xs text-stone-500 mb-4">{error}</p>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-lg bg-stone-800 text-white text-sm font-medium hover:bg-stone-700"
        >
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
        <PackageOpen size={32} className="mx-auto text-stone-300 mb-3" />
        <p className="text-sm font-semibold text-stone-700 mb-1">No builds published yet</p>
        <p className="text-xs text-stone-500 max-w-md mx-auto">
          release-service has no registered builds in this environment. Builds
          appear here automatically the next time CI publishes one — nothing
          to configure on this page.
        </p>
      </div>
    );
  }

  const truncated = rows.length > DISPLAY_LIMIT;
  const visible = truncated ? rows.slice(0, DISPLAY_LIMIT) : rows;

  return (
    <div className="space-y-3">
      {partialErrors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-800">
          Some app/environment pairs failed to load and are not shown: {partialErrors.join('; ')}
        </div>
      )}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Build</th>
                <th className="text-left px-4 py-2 font-semibold">Label</th>
                <th className="text-left px-4 py-2 font-semibold">App</th>
                <th className="text-left px-4 py-2 font-semibold">Env</th>
                <th className="text-left px-4 py-2 font-semibold">Target</th>
                <th className="text-left px-4 py-2 font-semibold">Version</th>
                <th className="text-left px-4 py-2 font-semibold">Comment</th>
                <th className="text-left px-4 py-2 font-semibold">Built</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {visible.map((b) => (
                <tr key={b._key} className="hover:bg-stone-50 align-top">
                  {/* The name a human says out loud. Derived by the SAME
                      buildShortId the service uses, so the id here can never
                      disagree with the one an API response prints. */}
                  <td className="px-4 py-2.5 font-mono font-semibold text-stone-800 whitespace-nowrap">
                    {buildShortId(b)}
                  </td>
                  <td className="px-4 py-2.5">
                    <BuildLabelCell row={b} onSave={(label) => saveLabel(b, label)} />
                  </td>
                  <td className="px-4 py-2.5 font-medium text-stone-700">{b.appId}</td>
                  <td className="px-4 py-2.5 text-stone-600">{b.env}</td>
                  <td className="px-4 py-2.5 text-stone-600">{b.target}</td>
                  {/* Kept, but demoted: this is the exact stamp the device
                      compares against, so it must stay visible and verbatim —
                      it is just no longer the thing you read first. */}
                  <td className="px-4 py-2.5 font-mono text-xs text-stone-500" title={b.hash || undefined}>
                    {b.version}
                  </td>
                  <td className="px-4 py-2.5 text-xs"><BuildCommentCell row={b} /></td>
                  <td className="px-4 py-2.5 text-stone-500 text-xs whitespace-nowrap">
                    {b.builtAt ? new Date(b.builtAt).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {truncated && (
          <div className="px-4 py-2 text-xs text-stone-400 border-t border-stone-100 bg-stone-50">
            Showing the newest {DISPLAY_LIMIT} of {rows.length} builds.
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildsTab;
